'use client';

/**
 * API namespace `bundles` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L6794-L7060).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const bundles = {
  /**
   * GET /api/seller/bundles?page=&limit=&status=&includeInactive= —
   * list the seller's own bundles (paginated, 1-indexed). Returns
   * `ProductBundleListResponse` (`bundles` + total + page + limit) —
   * each bundle is the EXPANDED snapshot (includes the `products[]`
   * array with title + priceCredits + thumbnail URL per product, plus
   * the seller's storeName + storeSlug snapshot — same wire shape as
   * the public catalog response per SPEC-242 §4.4).
   *
   * The `status` filter accepts any `ProductBundleStatus` value (or
   * undefined for "all statuses"). The `includeInactive` filter
   * defaults to `true` (the seller dashboard sees soft-deleted bundles
   * for audit) — pass `false` to exclude them.
   *
   * Authz: self only.
   *
   * Spec ref: SPEC-242 §4.4 (GET /seller/bundles), §4.5.1 (seller UI
   *           list table).
   */
  async list(
    page = 1,
    limit = 20,
    options?: { status?: ProductBundleStatus; includeInactive?: boolean },
    signal?: AbortSignal,
  ): Promise<ProductBundleListResponse> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (options?.status) params.set('status', options.status);
    if (options?.includeInactive !== undefined) {
      params.set('includeInactive', String(options.includeInactive));
    }
    return apiFetch<ProductBundleListResponse>(
      `/seller/bundles?${params.toString()}`,
      { signal },
    );
  },
  /**
   * GET /api/seller/bundles/:bundleId — seller's own view of a single
   * bundle (includes the `active` flag for soft-deleted bundles).
   *
   * Returns `ProductBundle` (the entity — NOT the public wire shape).
   * The seller view does NOT include the expanded `products[]` snapshot
   * (only the raw `productIds[]` array). For the expanded snapshot, the
   * seller UI uses the list endpoint which returns `ProductBundleResponse`
   * with `products[]` populated.
   *
   * Authz: self only — backend verifies the bundle belongs to the
   * authenticated seller (`sellerId === resolveSellerId(req.user!.sub)`).
   *
   * Spec ref: SPEC-242 §4.4 (GET /seller/bundles/:bundleId).
   */
  async get(
    bundleId: string,
    signal?: AbortSignal,
  ): Promise<ProductBundle> {
    return apiFetch<ProductBundle>(
      `/seller/bundles/${encodeURIComponent(bundleId)}`,
      { signal },
    );
  },
  /**
   * POST /api/seller/bundles — create a new bundle.
   *
   * Server defaults: `slug=slugify(title)` (retry on E11000 with `-2`,
   * `-3` suffix — same pattern as products), `status='pending_approval'`
   * (INV-MKT-20 — admin must approve before the bundle appears in the
   * public catalog), `active=true` (soft delete via the DELETE endpoint,
   * not via the create payload), `individualTotalCredits` +
   * `savingsCredits` + `savingsPercentage` CALCULATED server-side from
   * the `productIds` lookup.
   *
   * The client provides `bundlePriceCredits` (the seller's chosen
   * bundle price) — the server validates it's a positive decimal +
   * that it's LESS THAN the calculated `individualTotalCredits`
   * (SPEC-242 §4.3 — enforced in the service layer, NOT in the Zod
   * schema because the comparison requires the DB lookup).
   *
   * Authz: `requireAuth` + `requireActiveSeller`. Zod
   * `productBundleCreateSchema` cross-field rules (customFulfillment
   * required when fulfillment='custom') are enforced server-side.
   * Duplicate productIds ARE allowed (SPEC-242 §4.3 amended —
   * quantity-based combos like "cabin ×4 nights"). The same-seller
   * rule + the bundlePrice < individualTotal rule + the "all productIds
   * must be approved + same seller" rule are enforced in the service
   * layer (require DB lookup).
   *
   * Spec ref: SPEC-242 §4.4 (POST /seller/bundles), §4.3 (reglas de
   *           negocio), §4.5.1 (seller UI wizard — 4 pasos).
   */
  async create(
    data: ProductBundleCreateInput,
    signal?: AbortSignal,
    // Founder directive 2026-11-02 (combo sellerId passthrough bug —
    // Issue A BLOCKER): when `sellerId` is provided (the active store
    // selected via StoreSwitcher), append it as a query param so the BE
    // creates the bundle under the SELECTED sub-store (NOT the primary
    // store). Mirrors the seller-products.ts pattern (PR #1511 follow-up
    // + Founder directive 2026-10-31). The BE verifies ownership before
    // using it.
    sellerId?: string,
  ): Promise<ProductBundle> {
    const url = sellerId
      ? `/seller/bundles?sellerId=${encodeURIComponent(sellerId)}`
      : '/seller/bundles';
    return apiFetch<ProductBundle>(url, {
      method: 'POST',
      body: JSON.stringify(data),
      signal,
    });
  },
  /**
   * PUT /api/seller/bundles/:bundleId — partial update.
   *
   * Editing a bundle is only allowed when `status='pending_approval'`
   * OR `status='paused'` (SPEC-242 §4.3 "Editar bundle" rule — enforced
   * in the service layer). Editing an 'approved' bundle REQUIRES the
   * seller to first pause it (transition 'approved' → 'paused' via a
   * separate endpoint — future Task D #20).
   *
   * All `productBundleBaseSchema` fields are optional (partial update
   * via `.partial()`). The seller can edit any field (e.g., change
   * `productIds` to add/remove products, or change `fulfillment` from
   * 'inherits' to 'custom' — would require customFulfillment).
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only. Zod
   * `productBundleUpdateSchema` validates the partial body.
   *
   * Spec ref: SPEC-242 §4.4 (PUT /seller/bundles/:bundleId), §4.3
   *           ("Editar bundle" rule).
   */
  async update(
    bundleId: string,
    data: ProductBundleUpdateInput,
    signal?: AbortSignal,
    // Founder directive 2026-11-02 (combo sellerId passthrough bug —
    // Issue A BLOCKER): when `sellerId` is provided (the active store
    // selected via StoreSwitcher), append it as a query param so the BE
    // update + ownership check use the SELECTED sub-store. Mirrors the
    // seller-products.ts pattern (Founder directive 2026-10-31).
    sellerId?: string,
  ): Promise<ProductBundle> {
    const url = sellerId
      ? `/seller/bundles/${encodeURIComponent(bundleId)}?sellerId=${encodeURIComponent(sellerId)}`
      : `/seller/bundles/${encodeURIComponent(bundleId)}`;
    return apiFetch<ProductBundle>(
      url,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        signal,
      },
    );
  },
  /**
   * DELETE /api/seller/bundles/:bundleId — soft delete.
   *
   * Sets `active=false` (SPEC-242 §4.2 — NOT a 'deleted' status value,
   * unlike MarketProduct's soft-delete-via-status pattern). The bundle
   * is hidden from the public catalog but conserved for the audit
   * trail. The seller can still see it in their dashboard (with an
   * "Inactivo" indicator); admin can see it in the moderation panel.
   *
   * Idempotent: if the bundle is already `active=false`, the service
   * returns success without re-writing.
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only +
   * `requireRecent2FA` (TOTP 2FA verified within last 5 min — INV-SEC-06
   * §9.6 — soft-delete is irreversible + creates AuditLog
   * 'BUNDLE_DELETED'). The frontend opens `TwoFactorFinancialDialog` on
   * 403 `2FA_REQUIRED` (same pattern as the wallet withdraw + product
   * delete flows).
   *
   * Spec ref: SPEC-242 §4.4 (DELETE /seller/bundles/:bundleId), §4.2
   *           (`active` flag — soft delete);
   *           SPEC-NEGOCIO §9.6 (INV-SEC-06 — 2FA for sensitive ops).
   */
  async delete(
    bundleId: string,
    signal?: AbortSignal,
  ): Promise<{ bundleId: string; active: false }> {
    return apiFetch<{ bundleId: string; active: false }>(
      `/seller/bundles/${encodeURIComponent(bundleId)}`,
      { method: 'DELETE', signal },
    );
  },
  /**
   * GET /api/seller/bundles/:bundleId/stats — minimal summary view of
   * a bundle's pricing breakdown + productCount + status + timestamps.
   *
   * Returns `BundleStats` (locally defined above — mirrors the backend
   * service's interface verbatim). NO sales metrics in this task —
   * Fase 4 order/escrow is out of scope for Track D #19. Future tasks
   * (Track D #20+) will extend this shape with `totalSales`,
   * `totalOrders`, `totalRevenue` once the bundle purchase flow is
   * implemented.
   *
   * Authz: `requireAuth` + `requireActiveSeller` + self only.
   *
   * Spec ref: SPEC-242 §4.4 (GET /seller/bundles/:bundleId/stats).
   */
  async stats(
    bundleId: string,
    signal?: AbortSignal,
  ): Promise<BundleStats> {
    return apiFetch<BundleStats>(
      `/seller/bundles/${encodeURIComponent(bundleId)}/stats`,
      { signal },
    );
  },
  /**
   * Convenience constant — the 5 valid `ProductBundleStatus` values for
   * the seller dashboard status filter dropdown (avoids hard-coding the
   * union in the page component; the type itself is imported from
   * `@profileia/shared` so this array stays in sync with the union).
   *
   * NOTE: 5 values (NOT 6) — `ProductBundleStatus` does NOT include
   * 'deleted' (bundles use the `active: boolean` flag for soft delete
   * per SPEC-242 §4.2, NOT a status value — different from
   * `ProductStatus` which DOES include 'deleted').
   *
   * §6 Anti-Invención: the values mirror `ProductBundleStatus` verbatim
   * (Track D #17 — `types/product-bundle.ts`) — NOT invented here.
   *
   * Spec ref: SPEC-242 §4.2 (ProductBundleStatus enum), §4.5.1 (seller
   *           UI list table — status filter dropdown).
   */
  STATUS_VALUES: [
    'pending_approval',
    'approved',
    'rejected',
    'paused',
    'out_of_stock',
  ] as ReadonlyArray<ProductBundleStatus>,
};
